import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    phone: '',
    whatsapp: '',
    address: '',
    working_hours: '',
    about_text: '',
    hero_title: '',
    hero_subtitle: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      toast.error('Ayarlar yüklenemedi');
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ayarlar kaydedildi');
    } catch (error) {
      toast.error('Ayarlar kaydedilemedi');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Site Ayarları</h1>
        <p className="text-slate-600">Firma bilgileri ve genel site ayarlarını düzenleyin.</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6">
          {/* Company Info */}
          <Card>
            <CardHeader>
              <CardTitle>Firma Bilgileri</CardTitle>
            </CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name">Firma Adı</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={settings.company_name}
                  onChange={handleChange}
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefon</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={settings.phone}
                  onChange={handleChange}
                  data-testid="phone-input"
                />
              </div>
              <div>
                <Label htmlFor="whatsapp">WhatsApp Numarası</Label>
                <Input
                  id="whatsapp"
                  name="whatsapp"
                  value={settings.whatsapp}
                  onChange={handleChange}
                  placeholder="905XXXXXXXXX"
                  data-testid="whatsapp-input"
                />
              </div>
              <div>
                <Label htmlFor="working_hours">Çalışma Saatleri</Label>
                <Input
                  id="working_hours"
                  name="working_hours"
                  value={settings.working_hours}
                  onChange={handleChange}
                  data-testid="working-hours-input"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="address">Adres</Label>
                <Input
                  id="address"
                  name="address"
                  value={settings.address}
                  onChange={handleChange}
                  data-testid="address-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Hero Section */}
          <Card>
            <CardHeader>
              <CardTitle>Ana Sayfa Hero Bölümü</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="hero_title">Ana Başlık</Label>
                <Input
                  id="hero_title"
                  name="hero_title"
                  value={settings.hero_title}
                  onChange={handleChange}
                  data-testid="hero-title-input"
                />
              </div>
              <div>
                <Label htmlFor="hero_subtitle">Alt Başlık</Label>
                <Textarea
                  id="hero_subtitle"
                  name="hero_subtitle"
                  value={settings.hero_subtitle}
                  onChange={handleChange}
                  rows={3}
                  data-testid="hero-subtitle-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* About */}
          <Card>
            <CardHeader>
              <CardTitle>Hakkımızda Metni</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="about_text">Hakkımızda</Label>
                <Textarea
                  id="about_text"
                  name="about_text"
                  value={settings.about_text}
                  onChange={handleChange}
                  rows={5}
                  data-testid="about-text-input"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#002FA7] hover:bg-[#001d6e]"
              data-testid="save-settings-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </>
              )}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
